import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import useTranslate from '../../../lib/hooks/use-translate'
import {
  AccomodationTypeEnum,
  accomodationTypeEnumMapping
} from '../../../lib/types/enums/staff-application/accomodation-type.enum'
import {
  ConveyanceTypeEnum,
  conveyanceTypeEnumMapping
} from '../../../lib/types/enums/staff-application/conveyance-type.enum'
import { HostFamilyInput } from '../../../lib/types/models/host-family/host-family-input'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

const HostFamilyAccommodationDetailsForm = () => {
  const { t } = useTranslate()
  const form = useFormContext<HostFamilyInput>()

  return (
    <InnerBox className="p-4 rounded-xl">
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        Accommodation details
      </Typography>
      <div className="h-4" />
      <div className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="accomodationType"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required
                  label="Type of accommodation"
                  options={Object.values(AccomodationTypeEnum).map((status) => ({
                    label: t(accomodationTypeEnumMapping[status]),
                    value: status
                  }))}
                  selectedOption={getSelectedEnumOption(
                    field.value,
                    accomodationTypeEnumMapping,
                    t
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div>
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            With garden *
          </Typography>
          <div className="h-2" />
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="withGarden"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'Yes'}
                        checked={field.value === true}
                        onCheck={() => field.onChange(true)}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="withGarden"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'No'}
                        checked={field.value === false}
                        onCheck={() => field.onChange(false)}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          </div>
        </div>

        <FormField
          control={form.control}
          name="numberOfBedrooms"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  {...field}
                  required
                  type="number"
                  isControllable
                  allowNegative={false}
                  label="Number of bedrooms"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div>
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            Pets *
          </Typography>
          <div className="h-2" />
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="hasPets"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'Yes'}
                        checked={field.value === true}
                        onCheck={() => field.onChange(true)}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="hasPets"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'No'}
                        checked={field.value === false}
                        onCheck={() => field.onChange(false)}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          </div>
        </div>
        <FormField
          control={form.control}
          name="petDescription"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  {...field}
                  type="text"
                  label="Type and number of pets"
                  required={form.watch('hasPets') === true}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div>
          <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
            How to get to teaching hall / school *
          </Typography>
          <div className="h-2" />
          <div className="flex gap-4">
            {Object.values(ConveyanceTypeEnum).map((conveyanceType) => (
              <FormField
                key={conveyanceType}
                control={form.control}
                name="transportToSchool"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={t(conveyanceTypeEnumMapping[conveyanceType])}
                          checked={field.value === conveyanceType}
                          onCheck={() => field.onChange(conveyanceType)}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />
            ))}
          </div>
        </div>
        <FormField
          control={form.control}
          name="trainStation"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="text" label="Bus stop / Train station" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'timeToSchool'}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  type="number"
                  {...field}
                  required
                  label={'Travel time to the teaching hall / school (minutes)'}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="pickupPoint"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="text" label="Pick up point" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="hobbies"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="text" label="Hobbies" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="otherInformation"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="textarea" label="Other information" />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="dateOfVisit"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    required
                    label="Date of HF visits"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="visitedBy"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="text" label="Person who visited" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </InnerBox>
  )
}

export default HostFamilyAccommodationDetailsForm
